export class ApiError extends Error {
  status: number;
  details?: any;
  constructor(status: number, message: string, details?: any) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

/** Thrown when the server could not be reached at all (offline, server down). */
export class NetworkError extends Error {}

type Opts = { method?: string; body?: unknown; form?: FormData; signal?: AbortSignal };

export async function api<T = any>(path: string, opts: Opts = {}): Promise<T> {
  const method = opts.method ?? (opts.body !== undefined || opts.form ? 'POST' : 'GET');
  const headers: Record<string, string> = {};
  if (method !== 'GET') headers['x-trpg-csrf'] = '1';
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      body: opts.form ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
      credentials: 'same-origin',
      signal: opts.signal,
    });
  } catch (err: any) {
    if (err?.name === 'AbortError') throw err;
    throw new NetworkError('Cannot reach the server');
  }
  if (res.status === 401 && !path.startsWith('/api/auth/')) window.dispatchEvent(new Event('trpg:auth-required'));
  const type = res.headers.get('content-type') ?? '';
  const data = type.includes('application/json') ? await res.json().catch(() => null) : await res.text();
  if (!res.ok) {
    // A proxy answering while the app is down counts as unreachable for sync purposes.
    if (res.status === 502 || res.status === 503 || res.status === 504) throw new NetworkError(`Server unavailable (${res.status})`);
    throw new ApiError(res.status, (data && typeof data === 'object' && data.error) || `Request failed (${res.status})`, data?.details);
  }
  return data as T;
}

export function errorText(err: unknown): string {
  if (err instanceof NetworkError) return 'You are offline or the server is unreachable.';
  if (err instanceof Error) return err.message;
  return String(err);
}
